import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Search, Boxes, Pencil, Printer } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import NumberInput from "../../components/NumberInput";
import { openAuthenticatedPdf } from "../../utils/pdf";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtNum = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 2 });
const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

const expiryBadge = (expiry) => {
  if (!expiry) return null;
  const today = new Date().toISOString().slice(0, 10);
  const in7 = new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10);
  if (expiry < today) return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">SCADUTO</Badge>;
  if (expiry <= in7) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">IN SCADENZA</Badge>;
  return null;
};

const EditLotDialog = ({ lot, onClose, onSaved }) => {
  const [form, setForm] = useState(lot);
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/warehouse/inventory/lots/${lot.id}`, {
        description: form.description,
        unit: form.unit,
        quantity_kg: form.quantity_kg,
        pieces: form.pieces,
        supplier_lot: form.supplier_lot,
        expiry_date: form.expiry_date || null,
        origin: form.origin,
        notes: form.notes,
        born_in: form.born_in || "",
        raised_in: form.raised_in || "",
        slaughtered_in: form.slaughtered_in || "",
        slaughter_code: form.slaughter_code || "",
        cut_in: form.cut_in || "",
        cutting_code: form.cutting_code || "",
        slaughter_date: form.slaughter_date || null,
        ear_tag: form.ear_tag || "",
      });
      toast.success(`Partita ${lot.lot_number} aggiornata`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Modifica partita <span className="font-mono text-[#8A1C2A]">{lot.lot_number}</span></DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs text-stone-500">Descrizione</Label>
            <Input value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="lot-edit-description" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-stone-500">U.M.</Label>
              <Select value={form.unit} onValueChange={(v) => set({ unit: v })}>
                <SelectTrigger data-testid="lot-edit-unit"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="Kg">Kg</SelectItem><SelectItem value="Pz">Pz</SelectItem><SelectItem value="CT">CT</SelectItem></SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-stone-500">Peso tot. (Kg)</Label>
              <NumberInput value={form.quantity_kg} onChange={(e) => set({ quantity_kg: e.target.value })} data-testid="lot-edit-kg" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Colli / Pezzi</Label>
              <NumberInput value={form.pieces} onChange={(e) => set({ pieces: e.target.value })} data-testid="lot-edit-pieces" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-stone-500">Lotto fornitore</Label>
              <Input value={form.supplier_lot || ""} onChange={(e) => set({ supplier_lot: e.target.value })} data-testid="lot-edit-supplier-lot" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Scadenza (TMC)</Label>
              <Input type="date" value={form.expiry_date || ""} onChange={(e) => set({ expiry_date: e.target.value })} data-testid="lot-edit-expiry" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-stone-500">Origine / info HACCP</Label>
            <Input value={form.origin || ""} onChange={(e) => set({ origin: e.target.value })} placeholder="es. BLACK ANGUS" data-testid="lot-edit-origin" />
          </div>
          <p className="text-[11px] font-semibold text-stone-400 uppercase tracking-wide pt-1">Tracciabilità etichetta (HACCP)</p>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs text-stone-500">Nato in</Label>
              <Input value={form.born_in || ""} onChange={(e) => set({ born_in: e.target.value })} placeholder="ITALIA" data-testid="lot-edit-born" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Allevato in</Label>
              <Input value={form.raised_in || ""} onChange={(e) => set({ raised_in: e.target.value })} placeholder="ITALIA" data-testid="lot-edit-raised" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Macellato in</Label>
              <Input value={form.slaughtered_in || ""} onChange={(e) => set({ slaughtered_in: e.target.value })} placeholder="ITALIA" data-testid="lot-edit-slaughtered" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">N. macello</Label>
              <Input value={form.slaughter_code || ""} onChange={(e) => set({ slaughter_code: e.target.value })} placeholder="IT2270M" data-testid="lot-edit-slaughter-code" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Sezionato in</Label>
              <Input value={form.cut_in || ""} onChange={(e) => set({ cut_in: e.target.value })} placeholder="ITALIA" data-testid="lot-edit-cut" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">N. sezionamento</Label>
              <Input value={form.cutting_code || ""} onChange={(e) => set({ cutting_code: e.target.value })} placeholder="IT2270M" data-testid="lot-edit-cutting-code" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Data macellazione</Label>
              <Input type="date" value={form.slaughter_date || ""} onChange={(e) => set({ slaughter_date: e.target.value })} data-testid="lot-edit-slaughter-date" />
            </div>
            <div>
              <Label className="text-xs text-stone-500">Nr. auricolare</Label>
              <Input value={form.ear_tag || ""} onChange={(e) => set({ ear_tag: e.target.value })} data-testid="lot-edit-ear-tag" />
            </div>
          </div>
          <div>
            <Label className="text-xs text-stone-500">Note</Label>
            <Input value={form.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="lot-edit-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annulla</Button>
          <Button onClick={save} disabled={saving || !(form.description || "").trim()} className="bg-[#8A1C2A] hover:bg-[#701622]" data-testid="lot-edit-save-btn">{saving ? "Salvataggio…" : "Salva"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const KpiCard = ({ label, value, accent, testId }) => (
  <Card className={`p-4 bg-white border-stone-200 ${accent || ""}`} data-testid={testId}>
    <p className="text-2xl font-bold text-stone-900">{value}</p>
    <p className="text-xs text-stone-500 mt-0.5">{label}</p>
  </Card>
);

export default function InventoryLots() {
  const [lots, setLots] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [availability, setAvailability] = useState("available");
  const [expiring, setExpiring] = useState("none");
  const [editLot, setEditLot] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = { availability };
      if (search) params.search = search;
      if (expiring !== "none") params.expiring_days = expiring;
      const [lotsRes, statsRes] = await Promise.all([
        axios.get(`${API}/warehouse/inventory/lots`, { params }),
        axios.get(`${API}/warehouse/inventory/stats`),
      ]);
      setLots(lotsRes.data || []);
      setStats(statsRes.data);
    } catch { toast.error("Errore nel caricamento delle giacenze"); }
    finally { setLoading(false); }
  }, [search, availability, expiring]);

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  return (
    <div className="space-y-4" data-testid="inventory-lots-page">
      <div>
        <h1 className="text-2xl font-bold text-stone-900 flex items-center gap-2"><Boxes className="w-6 h-6 text-[#8A1C2A]" />Giacenze & Partite</h1>
        <p className="text-sm text-stone-500">Stock per partita con quantità residue e scadenze</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <KpiCard label="Partite disponibili" value={stats.lots_available} testId="lots-kpi-available" />
          <KpiCard label="Kg residui in magazzino" value={`${fmtNum(stats.available_kg)} Kg`} testId="lots-kpi-kg" />
          <KpiCard label="Colli / Pezzi residui" value={fmtNum(stats.available_pieces)} testId="lots-kpi-pieces" />
          <KpiCard label="In scadenza (7 gg)" value={stats.expiring_7_days} accent={stats.expiring_7_days > 0 ? "border-amber-300 bg-amber-50" : ""} testId="lots-kpi-expiring" />
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca partita, barcode, articolo, fornitore…" className="pl-9" data-testid="lots-search-input" />
        </div>
        <Select value={availability} onValueChange={setAvailability}>
          <SelectTrigger className="w-full sm:w-44" data-testid="lots-availability-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="available">✅ Disponibili</SelectItem>
            <SelectItem value="depleted">⬛ Esaurite</SelectItem>
            <SelectItem value="all">Tutte</SelectItem>
          </SelectContent>
        </Select>
        <Select value={expiring} onValueChange={setExpiring}>
          <SelectTrigger className="w-full sm:w-48" data-testid="lots-expiring-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Tutte le scadenze</SelectItem>
            <SelectItem value="0">⚠️ Già scadute</SelectItem>
            <SelectItem value="7">Entro 7 giorni</SelectItem>
            <SelectItem value="15">Entro 15 giorni</SelectItem>
            <SelectItem value="30">Entro 30 giorni</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border-stone-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[65vh] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
          <table className="w-full text-sm min-w-[960px]">
            <thead className="bg-stone-50 border-b border-stone-200 sticky top-0 z-10">
              <tr>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Partita</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Barcode</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Articolo</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Kg residui</th>
                <th className="text-right p-3 text-xs font-semibold text-stone-600">Colli/Pz res.</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Fornitore</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Carico</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Lotto forn.</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Scadenza</th>
                <th className="text-left p-3 text-xs font-semibold text-stone-600">Origine</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="11" className="p-8 text-center text-stone-500">Caricamento…</td></tr>
              ) : lots.length === 0 ? (
                <tr><td colSpan="11" className="p-8 text-center text-stone-500">Nessuna partita trovata</td></tr>
              ) : lots.map((l) => (
                <tr key={l.id} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`lot-row-${l.id}`}>
                  <td className="p-3 font-mono font-bold text-[#8A1C2A]">{l.lot_number}</td>
                  <td className="p-3 font-mono text-xs text-stone-500">{l.barcode}</td>
                  <td className="p-3">
                    <span className="text-xs font-mono text-stone-500">{l.product_code}</span>
                    <span className="block truncate max-w-[240px] font-medium">{l.description}</span>
                  </td>
                  <td className="p-3 text-right font-semibold">{l.quantity_kg > 0 ? `${fmtNum(l.remaining_kg)} / ${fmtNum(l.quantity_kg)}` : "—"}</td>
                  <td className="p-3 text-right">{l.pieces > 0 ? `${fmtNum(l.remaining_pieces)} / ${fmtNum(l.pieces)}` : "—"}</td>
                  <td className="p-3 truncate max-w-[140px]">{l.supplier_name || "—"}</td>
                  <td className="p-3 text-stone-600">n. {l.shipment_number}</td>
                  <td className="p-3">{l.supplier_lot || "—"}</td>
                  <td className="p-3">
                    <span className="flex items-center gap-1.5">{fmtDate(l.expiry_date)}{expiryBadge(l.expiry_date)}</span>
                  </td>
                  <td className="p-3 truncate max-w-[120px]">{l.origin || "—"}</td>
                  <td className="p-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => openAuthenticatedPdf(`/warehouse/inventory/lots/${l.id}/label-pdf`, { filename: `Etichetta_${(l.lot_number || "").replace("/", "-")}.pdf` })} className="text-stone-400 hover:text-[#8A1C2A]" title="Stampa etichetta 4x6" data-testid={`lot-label-${l.id}`}>
                        <Printer className="w-4 h-4" />
                      </button>
                      <button onClick={() => setEditLot(l)} className="text-stone-400 hover:text-[#8A1C2A]" title="Modifica" data-testid={`lot-edit-${l.id}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {editLot && <EditLotDialog lot={editLot} onClose={() => setEditLot(null)} onSaved={load} />}
    </div>
  );
}
